import { Controller } from '@hotwired/stimulus';
import { application } from 'src';
import { find, findAll, getParent, hasClass } from '@fretadao/f-js-dom';
import { API } from 'src/api';

application.register('b2b-admin-custom-routing', class extends Controller {
  static targets = [
    'mapContainer',
    'waypointLatLongWrapper',
    'routeInfoWrapper',
    'routingResponseField',
    'routeEtaEstimateField',
    'routeDistanceEstimateField',
  ];
  static values = {
    slug: String,
    editable: String
  };

  connect() {
    if (this.editableValue == 'false') {
      const routeEtaEl = find('.route-eta', this.routeInfoWrapperTarget);
      const routeDistEl = find('.route-dist', this.routeInfoWrapperTarget);
      const etaField = find(
        '#occasional_trip_request_route_eta_estimate_in_seconds',
        this.routeInfoWrapperTarget
      );
      const distanceField = find(
        '#occasional_trip_request_route_distance_estimate_in_meters',
        this.routeInfoWrapperTarget
      );

      routeEtaEl.innerHTML = this.#formatRouteEta(
        parseInt(etaField.value)
      );
      routeDistEl.innerHTML = this.#formatRouteDistance(
        parseInt(distanceField.value)
      );
    }
  }

  mapContainerTargetConnected() {
    this.waypointLatLongWrapperTargets.forEach((llWrapper) => {
      const inputs = findAll('input', llWrapper);
      inputs.forEach((input) => input.removeAttribute('value'));
    });
  }

  calculateRoute(event = null) {
    if(event) event.preventDefault(); // prevents accidental form submission

    this.#dispatchRenderWaypoints();

    if (this.#formattedWaypoints().length < 2) return;

    const csrfToken = find("meta[name='csrf-token']").content;
    const routingData = this.#formattedWaypoints().map((wp) => wp.coords);

    API.b2bAdmin.customRouting(csrfToken, this.slugValue, routingData)
    .then(({ data }) => {
        const routeEtaEl = find('.route-eta', this.routeInfoWrapperTarget);
        const routeDistEl = find('.route-dist', this.routeInfoWrapperTarget);
        const duration = parseInt(data.duration_in_seconds);
        const length = parseInt(data.length_in_meters);
        const polylineData = data.encoded_polylines;

        // formats and displays values on UI
        routeEtaEl.innerHTML = this.#formatRouteEta(duration);
        routeDistEl.innerHTML = this.#formatRouteDistance(length);

        // assigns values to hidden fields
        this.routingResponseFieldTarget.value = JSON.stringify(data.routing_response);
        this.routeEtaEstimateFieldTarget.value = duration;
        this.routeDistanceEstimateFieldTarget.value = length;

        // sends encoded polylines to map
        this.#dispatchRenderPolylines(polylineData);

        // dispatchs change event on form element for enable-submit controller
        // to pick up changes
        this.routeEtaEstimateFieldTarget.form.dispatchEvent(new Event('change'));
      })
      .catch((error) => {
        console.error(error);
        alert('Não foi possível calcular a rota!');
      })
  }

  reverseGeocode({ detail }) {
    const marker = detail.marker;
    const coords = [detail.new.lat, detail.new.lng];
    const csrfToken = find("meta[name='csrf-token']").content;

    API.b2bAdmin.routingLocate(csrfToken, this.slugValue, coords)
      .then(({ fullAddress }) => {
        const addressEl = find(`#${marker.options.fieldElId}`);
        const llWrapperEl = addressEl.closest('[data-b2b-admin-custom-routing-target="waypointLatLongWrapper"]');
        const latitudeEl = find('.latitude-field', llWrapperEl);
        const longitudeEl = find('.longitude-field', llWrapperEl);

        marker.title = fullAddress;
        addressEl.value = fullAddress;
        latitudeEl.value = coords[0];
        longitudeEl.value = coords[1];

        [addressEl, latitudeEl, longitudeEl].forEach((el) => el.dispatchEvent(new Event('change')));
      })
      .catch((error) => {
        console.error(error);
        alert('Não foi possível encontrar o endereço do ponto de parada!');
      })
  }

  #formattedWaypoints() {
    const lls = this.waypointLatLongWrapperTargets.map((llWrapperEl) => {
      if (hasClass(llWrapperEl, 'removed-waypoint')) {
        return null;
      }

      const addressEl = find('.address-field', llWrapperEl);
      const lat = find('.latitude-field', llWrapperEl)?.value;
      const long = find('.longitude-field', llWrapperEl)?.value;
      const identifier = addressEl?.id;
      const title = addressEl?.value;

      if (!lat || !long) return null;

      return { identifier: identifier, title: title, coords: [lat, long]};
    });

    return lls.filter(val => val);
  }

  #formatRouteEta(duration = 0) {
    const d = Number(duration);
    const h = Math.floor(d / 3600);
    const m = Math.floor((d % 3600) / 60);
    const mDisplay = m > 0 ? `${String(m).padStart(2, '0')}` : '00';
    const diff = h != 0 ? `≃${h}h${mDisplay}min` : `≃${mDisplay}min`;

    return diff;
  }

  #formatRouteDistance(distance = 0) {
    if (distance > 1000) return `≃${Math.round(distance / 1000)}km`;
    else return `≃${Math.round(distance)}m`;
  }

  #dispatchRenderPolylines(polylines) {
    this.mapContainerTarget.dispatchEvent(new CustomEvent('plotPolyline', {
      detail: polylines
    }));
  }

  #dispatchRenderWaypoints(waypoints = this.#formattedWaypoints()) {
    this.mapContainerTarget.dispatchEvent(new CustomEvent('onGeoInputSelectAddress', {
      detail: waypoints
    }));
  }
});
